import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './SubMenu';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';
import { AccrualProfile } from 'repository/timekeeper/@types/AccrualProfile';

type ResponseAccrualProfile = AccrualProfile;

const AccrualProfileTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedAccrualProfile, setSelectedAccrualProfile] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<any>([]);

    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const columns: GridColDef[] = [
        { field: 'name', headerName: t("accruals.accrual_profile.table.columns.name"), flex: 1 },
        { field: 'visible_rules', headerName: t("accruals.accrual_profile.table.columns.visible_rules"), flex: 1, headerAlign: 'center', align: 'center' },
        { field: 'hidden_rules', headerName: t("accruals.accrual_profile.table.columns.hidden_rules"), flex: 1, headerAlign: 'center', align: 'center'},
        { field: 'accrual_fund', headerName: t("accruals.accrual_profile.table.columns.accrual_fund"), flex: 1, headerAlign: 'center', align: 'center', filterable: false},
        { field: 'group_of_balance_sheets_with_domino_effect', headerName: t("accruals.accrual_profile.table.columns.group_of_balance_sheets_with_domino_effect"), flex: 1, headerAlign: 'center', align: 'center'},
    ];

    const reloading = useCallback(() => {
        setLoading(true);
        timekeeperService.accrual_profile.getAll({ include: 'accrual_code,group_balances,accrual_rules' })
            .then((data: ResponseAccrualProfile[]) => {
                console.log('API Response:', data);

                const accrual_profiles = data.map((accrual_profile, index) => {
                    // Filtrar reglas visibles (show === true)
                    const visibleRules = accrual_profile.accrual_rules?.filter(rule => rule.show === true) || [];
                    // Filtrar reglas ocultas (show === false)
                    const hiddenRules = accrual_profile.accrual_rules?.filter(rule => rule.show === false) || [];
                    // Obtener grupos de balance
                    const groupBalances = accrual_profile.group_balances || [];

                    console.log(`Profile "${accrual_profile.name}" - Visible rules:`, visibleRules.map(r => r.name));
                    console.log(`Profile "${accrual_profile.name}" - Hidden rules:`, hiddenRules.map(r => r.name));

                    return {
                        id: accrual_profile.accrual_profile_id,
                        name: accrual_profile.name,
                        visible_rules: visibleRules.length > 0 ? visibleRules.map(r => r.name).join(', ') : '-',
                        hidden_rules: hiddenRules.length > 0 ? hiddenRules.map(r => r.name).join(', ') : '-',
                        accrual_fund: accrual_profile.accrual_code ? accrual_profile.accrual_code.name : '-',
                        group_of_balance_sheets_with_domino_effect: groupBalances.length > 0 ? groupBalances.map(gr => gr.name).join(', ') : '-',
                    };
                });
                setRows(accrual_profiles);
            })
            .catch((error) => {
                console.error('Error al obtener los feriados:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timekeeperService.accrual_profile]);

    useEffect(() => {
        reloading();
    }, [reloading]);

    const cleanSelected = () => {
        setSelected([]);
        setSelectedAccrualProfile([]);
    }

    return (
        <div style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                rowSelectionModel={selectedAccrualProfile}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let row = rows.filter((row) => newRowSelectionModel.includes(row.id))
                    setSelectedAccrualProfile(newRowSelectionModel);
                    setSelected(row);

                }}
                checkboxSelection
                localeText={
                    {
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1 ? `${count.toLocaleString()} filas seleccionadas`
                                : `${count.toLocaleString()} fila seleccionada`,

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }
                }
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    }
                }}
            />
            <SubMenu
                selected={selected}
                cleanSelected={cleanSelected}
                rows={rows}
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
            />
        </div>
    );
}

export { AccrualProfileTable };